import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
import PageSEO from '../components/PageSEO';
import SiteFooter from '../components/SiteFooter';
import PhoneChat from '../components/landing/PhoneChat';
import HowItWorks from '../components/landing/HowItWorks';
import {
  SMS_DEEP_LINK,
  SMS_DISPLAY_NUMBER,
  SMS_BODY,
  HERO_HEADLINE,
  HERO_SUBHEAD,
  COMPLIANCE_COPY,
  TRUST_SIGNALS,
  HOW_IT_WORKS_STEPS,
  FAQ_ITEMS,
  HERO_CONVERSATION,
} from '../components/landing/constants';

const homepageJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Ask Aspen',
    url: 'https://www.askaspen.app',
    logo: 'https://www.askaspen.app/aspen-logomark.webp',
    description: 'Personal AI companion who remembers what matters to you and texts it back at the right moment',
    sameAs: [
      'https://twitter.com/AskAspenApp',
      'https://www.linkedin.com/company/ask-aspen',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: 'https://www.askaspen.app/contact',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Ask Aspen',
    applicationCategory: 'ProductivityApplication',
    operatingSystem: 'Web',
    url: 'https://www.askaspen.app',
    description: 'Personal AI companion for conversation, memory, reminders, and optional follow-up by text messaging.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Ask Aspen',
    url: 'https://www.askaspen.app',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Use Ask Aspen by Text',
    description: 'Get started with Ask Aspen in three simple steps.',
    step: HOW_IT_WORKS_STEPS.map((s) => ({
      '@type': 'HowToStep',
      name: s.title,
      text: s.description,
    })),
  },
];

const LandingPage: React.FC = () => {
  const complianceId = 'landing-sms-compliance';
  const complianceFinalId = 'landing-sms-compliance-final';
  const { track } = useAnalytics();
  const [showStickyMobileCta, setShowStickyMobileCta] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    track('landing_cta_view');

    if (typeof window === 'undefined') {
      return;
    }

    // Sticky mobile CTA appears once the hero has scrolled away.
    const handleScroll = () => {
      setShowStickyMobileCta(window.scrollY > 560);
    };

    window.addEventListener('scroll', handleScroll);

    let hasTrackedQr = false;
    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const maybeTrackQr = (target: MediaQueryList | MediaQueryListEvent) => {
      if (target.matches && !hasTrackedQr) {
        track('qr_render');
        hasTrackedQr = true;
      }
    };

    maybeTrackQr(mediaQuery);
    const listener = (event: MediaQueryListEvent) => maybeTrackQr(event);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', listener);
    } else {
      mediaQuery.addListener(listener);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', listener);
      } else {
        mediaQuery.removeListener(listener);
      }
    };
  }, [track]);

  const handleCtaClick = () => {
    track('sms_cta_click');
  };

  const handleQrClick = () => {
    track('qr_scan_click');
  };

  const handleCopyNumber = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(SMS_DISPLAY_NUMBER).catch(() => undefined);
    }
    track('phone_number_copied');
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  // The single, repeated conversion action.
  const primaryCtaClasses =
    'inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-aspen-vibrant-blue px-8 py-4 text-lg font-semibold text-white shadow-[0_8px_24px_rgba(37,99,235,0.35)] transition-all hover:-translate-y-0.5 hover:bg-aspen-vibrant-blue/90 hover:shadow-[0_12px_32px_rgba(37,99,235,0.45)] focus:outline-none focus:ring-2 focus:ring-aspen-vibrant-blue focus:ring-offset-2 focus:ring-offset-aspen-warm-ivory dark:focus:ring-offset-aspen-night-sage min-h-[52px]';

  return (
    <div className="min-h-screen bg-aspen-warm-ivory dark:bg-aspen-night-sage">
      <PageSEO
        title="Ask Aspen: The Personal AI That Texts You First"
        description="A warm AI conversation by text, useful details you choose to save, and reminders when you ask. Free to start, no app to download."
        canonicalPath="/"
        jsonLd={homepageJsonLd}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-aspen-blush-clay/30 bg-aspen-warm-ivory/85 backdrop-blur-md dark:border-aspen-moonlit-clay/30 dark:bg-aspen-night-sage/85">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <Link to="/" className="flex items-center" aria-label="Ask Aspen home">
              <img
                src="/aspen-logomark.webp"
                alt="Ask Aspen logo"
                className="h-6 w-auto sm:h-8"
                width={32}
                height={32}
              />
              <span className="ml-2 text-lg sm:ml-3 sm:text-xl font-semibold text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Ask Aspen
              </span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link
                to="/how-it-works"
                className="text-aspen-deep-eucalyptus dark:text-aspen-pearl hover:text-aspen-sage-teal transition-colors"
              >
                How It Works
              </Link>
              <Link
                to="/features"
                className="text-aspen-deep-eucalyptus dark:text-aspen-pearl hover:text-aspen-sage-teal transition-colors"
              >
                Features
              </Link>
              <Link
                to="/pricing"
                className="text-aspen-deep-eucalyptus dark:text-aspen-pearl hover:text-aspen-sage-teal transition-colors"
              >
                Pricing
              </Link>
              <a
                href={SMS_DEEP_LINK}
                onClick={handleCtaClick}
                aria-describedby={complianceId}
                className="bg-aspen-vibrant-blue hover:bg-aspen-vibrant-blue/90 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Text Aspen Free
              </a>
            </div>
            <div className="md:hidden flex items-center">
              <a
                href={SMS_DEEP_LINK}
                onClick={handleCtaClick}
                aria-describedby={complianceId}
                className="bg-aspen-vibrant-blue hover:bg-aspen-vibrant-blue/90 text-white px-3 py-2 rounded-lg transition-colors text-sm font-medium"
              >
                Text Aspen
              </a>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-aspen-warm-ivory via-aspen-morning-mist to-aspen-warm-ivory dark:from-aspen-night-sage dark:via-aspen-twilight-teal dark:to-aspen-night-sage">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
            <div className="pt-8 pb-12 sm:py-16 lg:py-24">
              <span className="hidden sm:inline-flex items-center gap-2 px-4 py-1.5 mb-5 text-xs sm:text-sm font-semibold rounded-full bg-aspen-sage-teal/10 text-aspen-sage-teal dark:bg-aspen-sage-teal/20 dark:text-aspen-gentle-teal border border-aspen-sage-teal/20">
                <span className="h-2 w-2 rounded-full bg-aspen-vibrant-green" />
                Your personal AI companion, over text
              </span>
              <h1 className="text-4xl tracking-tight font-bold text-aspen-deep-eucalyptus dark:text-aspen-pearl sm:text-5xl md:text-6xl">
                {HERO_HEADLINE}
              </h1>

              {/* The number is the product. Keep it above the fold on every screen. */}
              <div className="mt-5 sm:mt-6">
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-widest text-aspen-sage-teal dark:text-aspen-gentle-teal">
                  Save this number.<span className="hidden sm:inline"> Everything else is just texting.</span>
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-2">
                  <a
                    href={SMS_DEEP_LINK}
                    onClick={handleCtaClick}
                    aria-describedby={complianceId}
                    className="font-bold tracking-tight tabular-nums text-aspen-vibrant-blue transition-colors hover:text-aspen-deep-eucalyptus dark:text-aspen-gentle-teal dark:hover:text-aspen-pearl text-[2.125rem] leading-tight sm:text-5xl lg:text-6xl"
                  >
                    {SMS_DISPLAY_NUMBER}
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyNumber}
                    className="inline-flex items-center justify-center rounded-xl border border-aspen-sage-teal/40 px-2.5 sm:px-3 py-1.5 text-sm font-semibold text-aspen-sage-teal transition-colors hover:bg-aspen-sage-teal/10 dark:text-aspen-gentle-teal min-h-[36px]"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <p className="mt-5 text-lg text-aspen-slate-blue dark:text-aspen-lavender-gray sm:text-xl max-w-xl">
                {HERO_SUBHEAD}
              </p>

              <div className="mt-8 max-w-md">
                <a
                  href={SMS_DEEP_LINK}
                  onClick={handleCtaClick}
                  aria-describedby={complianceId}
                  className={primaryCtaClasses}
                >
                  <span aria-hidden="true">💬</span>
                  Text “{SMS_BODY}”. It&apos;s free.
                </a>

                <div className="mt-3 text-center">
                  <Link
                    to="/app"
                    className="text-sm text-aspen-sage-teal dark:text-aspen-gentle-teal hover:text-aspen-deep-eucalyptus dark:hover:text-aspen-pearl transition-colors inline-flex items-center min-h-[44px] px-3"
                  >
                    Prefer to point and click? Open the web app →
                  </Link>
                </div>

                {/* Trust Signals */}
                <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
                  {TRUST_SIGNALS.map((signal) => (
                    <li
                      key={signal}
                      className="flex items-center text-sm font-medium text-aspen-deep-eucalyptus dark:text-aspen-pearl"
                    >
                      <svg className="h-5 w-5 text-aspen-vibrant-green mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {signal}
                    </li>
                  ))}
                </ul>

                {/* Desktop QR — matters where you can't tap to text */}
                <div className="mt-6 hidden lg:flex items-center gap-4 rounded-2xl border border-aspen-blush-clay/40 bg-white/70 p-4 dark:border-aspen-moonlit-clay/40 dark:bg-aspen-twilight-teal/70">
                  <a
                    href={SMS_DEEP_LINK}
                    onClick={handleQrClick}
                    aria-label="Scan this QR code to text Aspen with a pre-filled message"
                    aria-describedby={complianceId}
                    className="rounded-xl border border-aspen-moonlit-clay/20 bg-white p-2 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-aspen-vibrant-blue"
                  >
                    <img
                      src="/qr-sms-link.svg"
                      width={96}
                      height={96}
                      alt="QR code that opens a text to Aspen with “Hey Aspen” pre-filled"
                      className="h-24 w-24"
                    />
                  </a>
                  <div>
                    <p className="text-sm font-semibold text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                      On a computer? Scan to text from your phone.
                    </p>
                    <p className="mt-1 text-sm text-aspen-slate-blue dark:text-aspen-lavender-gray">
                      Opens a message with &ldquo;{SMS_BODY}&rdquo; ready to send.
                    </p>
                  </div>
                </div>

                <p
                  id={complianceId}
                  className="mt-4 text-xs leading-5 text-aspen-slate-blue/80 dark:text-aspen-lavender-gray/80"
                >
                  {COMPLIANCE_COPY}{' '}
                  <Link to="/privacy" className="font-medium text-aspen-sage-teal underline-offset-2 hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* Hero screenshot */}
            <div className="relative flex items-center justify-center pb-12 lg:py-24">
              <div className="pointer-events-none absolute inset-0 -z-0 mx-auto h-64 w-64 rounded-full bg-aspen-sage-teal/20 blur-3xl sm:h-80 sm:w-80" aria-hidden="true" />
              <PhoneChat
                messages={HERO_CONVERSATION}
                label="A text thread with Aspen: Aspen texts first to ask how your presentation went, remembers you wanted to celebrate, and offers a nudge that evening."
                timeLabel="Today 6:12 PM"
                statusTime="6:12"
                loop
                className="relative z-10 w-56 sm:w-64 lg:w-72"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 sm:py-20 bg-white dark:bg-aspen-twilight-teal scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-base text-aspen-vibrant-blue font-semibold tracking-wide uppercase">How It Works</h2>
            <p className="mt-2 text-3xl leading-tight font-bold tracking-tight text-aspen-deep-eucalyptus dark:text-aspen-pearl sm:text-4xl">
              One text now. The right nudge later.
            </p>
          </div>

          <HowItWorks />

          <div className="mt-14 flex justify-center">
            <a
              href={SMS_DEEP_LINK}
              onClick={handleCtaClick}
              aria-describedby={complianceId}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-aspen-vibrant-blue px-8 py-4 text-lg font-semibold text-white shadow-[0_8px_24px_rgba(37,99,235,0.35)] transition-all hover:-translate-y-0.5 hover:bg-aspen-vibrant-blue/90 min-h-[52px]"
            >
              <span aria-hidden="true">💬</span> Text {SMS_DISPLAY_NUMBER} to start free
            </a>
          </div>
        </div>
      </section>

      {/* Why it works / objection handling */}
      <section className="py-16 sm:py-20 bg-aspen-warm-ivory dark:bg-aspen-night-sage">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-base text-aspen-vibrant-blue font-semibold tracking-wide uppercase">Why It Helps</h2>
            <p className="mt-2 text-3xl leading-tight font-bold tracking-tight text-aspen-deep-eucalyptus dark:text-aspen-pearl sm:text-4xl">
              A little continuity, on your terms
            </p>
            <p className="mt-4 text-lg text-aspen-slate-blue dark:text-aspen-lavender-gray">
              Aspen lives in your text messages, so the dates, details, and promises you&apos;d otherwise carry around finally have somewhere to go.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: 'Your head gets lighter',
                body: 'Hand over the thing you keep re-remembering. It stops being yours to hold.',
              },
              {
                title: 'They already know your context',
                body: 'No re-explaining who Dad is, which gym, or what Tuesdays look like. Correct or forget any detail whenever you want.',
              },
              {
                title: 'They show up when it counts',
                body: 'Not another pile of notifications. The right nudge at the right hour, and only the ones you asked for.',
              },
              {
                title: 'You are never locked in',
                body: 'Nothing to install, free with no credit card, and one word ends it: STOP.',
              },
            ].map((benefit) => (
              <div
                key={benefit.title}
                className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-aspen-blush-clay/30 transition-transform hover:-translate-y-1 dark:bg-aspen-twilight-teal dark:ring-aspen-moonlit-clay/30"
              >
                <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-aspen-sage-teal/10 text-aspen-sage-teal dark:bg-aspen-sage-teal/20 dark:text-aspen-gentle-teal">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                  {benefit.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-aspen-slate-blue dark:text-aspen-gentle-teal">
                  {benefit.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-aspen-warm-ivory dark:bg-aspen-night-sage">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-aspen-vibrant-blue font-semibold tracking-wide uppercase">FAQ</h2>
            <p className="mt-2 text-3xl leading-tight font-bold tracking-tight text-aspen-deep-eucalyptus dark:text-aspen-pearl sm:text-4xl">
              Everything you need to know
            </p>
          </div>

          <dl className="space-y-4">
            {FAQ_ITEMS.map((item) => (
              <div key={item.question} className="bg-white dark:bg-aspen-twilight-teal rounded-xl p-6 shadow-sm ring-1 ring-aspen-blush-clay/20 dark:ring-aspen-moonlit-clay/20">
                <dt className="text-lg font-semibold text-aspen-deep-eucalyptus dark:text-aspen-pearl mb-2">
                  {item.question}
                </dt>
                <dd className="text-base text-aspen-slate-blue dark:text-aspen-gentle-teal">
                  {item.answer}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-aspen-vibrant-blue to-blue-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl font-bold text-white sm:text-4xl">
                Give your brain a break. It&apos;s one text.
              </h2>
              <p className="mt-4 text-xl text-blue-100">
                Say hello, think out loud, or hand off the thing you keep almost forgetting. Free to start, nothing to install, one word to stop.
              </p>
              <p className="mt-6 text-blue-100 text-sm uppercase tracking-widest font-semibold">Text &ldquo;{SMS_BODY}&rdquo; to</p>
              <p className="text-4xl sm:text-5xl font-bold text-white tracking-tight">{SMS_DISPLAY_NUMBER}</p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <a
                  href={SMS_DEEP_LINK}
                  onClick={handleCtaClick}
                  aria-describedby={complianceFinalId}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 text-lg font-semibold text-aspen-vibrant-blue shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl min-h-[52px]"
                >
                  <span aria-hidden="true">📱</span> Text Aspen, start free
                </a>
                <Link
                  to="/app"
                  className="inline-flex items-center justify-center rounded-2xl border-2 border-white/70 px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-white hover:text-aspen-vibrant-blue min-h-[52px]"
                >
                  Use the web app →
                </Link>
              </div>

              <p id={complianceFinalId} className="mt-6 text-xs leading-5 text-blue-100/90 max-w-xl mx-auto lg:mx-0">
                {COMPLIANCE_COPY}{' '}
                <Link to="/privacy" className="font-medium text-white underline underline-offset-2">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>

            {/* Desktop QR */}
            <div className="hidden lg:flex justify-center">
              <div className="rounded-3xl bg-white p-8 text-center shadow-2xl">
                <p className="text-sm font-semibold uppercase tracking-widest text-aspen-slate-blue">
                  On a computer? Scan to text
                </p>
                <a
                  href={SMS_DEEP_LINK}
                  onClick={handleQrClick}
                  aria-label="Scan this QR code to text Aspen with a pre-filled message"
                  aria-describedby={complianceFinalId}
                  className="mt-4 inline-flex rounded-2xl border border-aspen-moonlit-clay/20 p-3 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-aspen-vibrant-blue"
                >
                  <img
                    src="/qr-sms-link.svg"
                    width={176}
                    height={176}
                    alt="QR code that opens a text to Aspen with “Hey Aspen” pre-filled"
                    className="h-44 w-44"
                  />
                </a>
                <p className="mt-4 mx-auto max-w-xs text-sm text-aspen-slate-blue">
                  Opens a message with &ldquo;{SMS_BODY}&rdquo; ready to send.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />

      {/* Sticky Mobile CTA */}
      {showStickyMobileCta && (
        <div className="fixed bottom-4 left-4 right-4 z-50 md:hidden">
          <a
            href={SMS_DEEP_LINK}
            onClick={handleCtaClick}
            aria-describedby={complianceId}
            className="flex items-center justify-center gap-2 w-full rounded-2xl bg-aspen-vibrant-blue px-6 py-4 text-base font-semibold text-white shadow-[0_8px_28px_rgba(37,99,235,0.5)] transition-all hover:bg-aspen-vibrant-blue/90 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 min-h-[52px]"
          >
            <span aria-hidden="true">💬</span> Text &ldquo;{SMS_BODY}&rdquo;. It&apos;s free.
          </a>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
